import * as THREE from "three";

export interface OrthoSetup {
  scene: THREE.Scene;
  camera: THREE.OrthographicCamera;
  renderer: THREE.WebGLRenderer;
  virtualHeight: number;
  setBackground(hex: number): void;
  worldFromEvent(ev: PointerEvent | MouseEvent): THREE.Vector2;
  dispose(): void;
}

export function setupOrtho(
  canvas: HTMLCanvasElement,
  opts: { virtualHeight?: number; background?: number } = {},
): OrthoSetup {
  const virtualHeight = opts.virtualHeight ?? 18;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(opts.background ?? 0x1b1428);

  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
  camera.position.set(0, 0, 10);

  function applySize() {
    const parent = canvas.parentElement!;
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    renderer.setSize(w, h, false);
    const aspect = w / h;
    camera.left = (-virtualHeight * aspect) / 2;
    camera.right = (virtualHeight * aspect) / 2;
    camera.top = virtualHeight / 2;
    camera.bottom = -virtualHeight / 2;
    camera.updateProjectionMatrix();
  }
  applySize();
  const onResize = () => applySize();
  window.addEventListener("resize", onResize);

  function worldFromEvent(ev: PointerEvent | MouseEvent): THREE.Vector2 {
    const rect = canvas.getBoundingClientRect();
    const nx = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -(((ev.clientY - rect.top) / rect.height) * 2 - 1);
    const vec = new THREE.Vector3(nx, ny, 0).unproject(camera);
    return new THREE.Vector2(vec.x, vec.y);
  }

  return {
    scene,
    camera,
    renderer,
    virtualHeight,
    setBackground(hex) {
      (scene.background as THREE.Color).setHex(hex);
    },
    worldFromEvent,
    dispose() {
      window.removeEventListener("resize", onResize);
      renderer.dispose();
    },
  };
}

export function makeRoundedRect(
  width: number,
  height: number,
  radius: number,
  color: number,
): THREE.Mesh {
  const shape = new THREE.Shape();
  const x = -width / 2;
  const y = -height / 2;
  shape.moveTo(x + radius, y);
  shape.lineTo(x + width - radius, y);
  shape.quadraticCurveTo(x + width, y, x + width, y + radius);
  shape.lineTo(x + width, y + height - radius);
  shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  shape.lineTo(x + radius, y + height);
  shape.quadraticCurveTo(x, y + height, x, y + height - radius);
  shape.lineTo(x, y + radius);
  shape.quadraticCurveTo(x, y, x + radius, y);
  const geom = new THREE.ShapeGeometry(shape);
  const mat = new THREE.MeshBasicMaterial({ color });
  return new THREE.Mesh(geom, mat);
}

export function makeCircle(radius: number, color: number, segments = 32): THREE.Mesh {
  const geom = new THREE.CircleGeometry(radius, segments);
  const mat = new THREE.MeshBasicMaterial({ color });
  return new THREE.Mesh(geom, mat);
}

export function makeRing(
  innerRadius: number,
  outerRadius: number,
  color: number,
  segments = 32,
): THREE.Mesh {
  const geom = new THREE.RingGeometry(innerRadius, outerRadius, segments);
  const mat = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
  return new THREE.Mesh(geom, mat);
}

export function tween(
  obj: THREE.Object3D,
  to: { x?: number; y?: number; z?: number; sx?: number; sy?: number },
  durationMs: number,
  onDone?: () => void,
): void {
  const start = performance.now();
  const fromX = obj.position.x;
  const fromY = obj.position.y;
  const fromZ = obj.position.z;
  const fromSX = obj.scale.x;
  const fromSY = obj.scale.y;
  const toX = to.x ?? fromX;
  const toY = to.y ?? fromY;
  const toZ = to.z ?? fromZ;
  const toSX = to.sx ?? fromSX;
  const toSY = to.sy ?? fromSY;

  function step(now: number) {
    const t = Math.min(1, (now - start) / durationMs);
    const e = easeOutCubic(t);
    obj.position.set(
      fromX + (toX - fromX) * e,
      fromY + (toY - fromY) * e,
      fromZ + (toZ - fromZ) * e,
    );
    obj.scale.x = fromSX + (toSX - fromSX) * e;
    obj.scale.y = fromSY + (toSY - fromSY) * e;
    if (t < 1) requestAnimationFrame(step);
    else onDone?.();
  }
  requestAnimationFrame(step);
}

export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
